import { useEffect, useState, useMemo, useCallback } from "react";
import { SlidersHorizontal, X, History as HistoryIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState } from "@/components/common";
import { ControlDetail } from "@/components/ControlDetail";
import { api } from "@/lib/api";
import { fmtDate, fmtTime, fmtTemp } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { location_id: "", area_id: "", equipment_id: "", shift_id: "", user_id: "", result: "", incident: "", date_from: "", date_to: "" };

export function HistoryView({ isAdmin = false }) {
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [opts, setOpts] = useState({ locations: [], areas: [], equipment: [], shifts: [], users: [] });

  useEffect(() => {
    const calls = [
      api.get("/locations").then((r) => ["locations", r.data]).catch(() => ["locations", []]),
      api.get("/areas").then((r) => ["areas", r.data]).catch(() => ["areas", []]),
      api.get("/equipment").then((r) => ["equipment", r.data]).catch(() => ["equipment", []]),
      api.get("/shifts").then((r) => ["shifts", r.data]).catch(() => ["shifts", []]),
    ];
    if (isAdmin) calls.push(api.get("/users").then((r) => ["users", r.data]).catch(() => ["users", []]));
    Promise.all(calls).then((pairs) => setOpts((o) => ({ ...o, ...Object.fromEntries(pairs) })));
  }, [isAdmin]);

  const queryString = useCallback(() => {
    const p = new URLSearchParams({ limit: "300" });
    if (filters.location_id) p.append("location_id", filters.location_id);
    if (filters.area_id) p.append("area_id", filters.area_id);
    if (filters.equipment_id) p.append("equipment_id", filters.equipment_id);
    if (filters.shift_id) p.append("shift_id", filters.shift_id);
    if (filters.user_id) p.append("user_id", filters.user_id);
    if (filters.result) p.append("in_range", filters.result === "in" ? "true" : "false");
    if (filters.incident) p.append("incident", filters.incident);
    if (filters.date_from) p.append("date_from", filters.date_from);
    if (filters.date_to) p.append("date_to", filters.date_to);
    return p.toString();
  }, [filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/temperature-checks?${queryString()}`)
      .then(({ data }) => setChecks(data)).catch(() => setChecks([]))
      .finally(() => setLoading(false));
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  const activeCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);
  const areas = filters.location_id ? opts.areas.filter((a) => a.location_id === filters.location_id) : opts.areas;
  const equipment = opts.equipment.filter((e) =>
    (!filters.location_id || e.location_id === filters.location_id) && (!filters.area_id || e.area_id === filters.area_id));
  const shifts = filters.location_id ? opts.shifts.filter((s) => s.location_id === filters.location_id) : opts.shifts;

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === "__all" ? "" : v }));

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Button data-testid="open-filters-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-xl">
          <SlidersHorizontal className="mr-2 h-4 w-4" /> Filtros{activeCount > 0 && <span className="ml-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-xs text-primary-foreground">{activeCount}</span>}
        </Button>
        {activeCount > 0 && (
          <Button data-testid="clear-filters-inline-btn" variant="ghost" onClick={() => setFilters(EMPTY)} className="rounded-xl text-slate-500">
            <X className="mr-1 h-4 w-4" /> Limpiar
          </Button>
        )}
        <span className="ml-auto text-sm text-slate-400">{checks.length} registro(s)</span>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400">Cargando…</div>
      ) : checks.length === 0 ? (
        <EmptyState icon={HistoryIcon} title="Sin resultados" subtitle="No hay registros para estos filtros." />
      ) : (
        <div className="space-y-3">
          {checks.map((c) => (
            <button key={c.id} data-testid={`history-item-${c.id}`} onClick={() => setDetailId(c.id)}
              className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left hover:shadow-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{c.equipment_name}</p>
                <p className="truncate text-xs text-slate-500">{fmtDate(c.created_at)} · {fmtTime(c.created_at)} · {c.location_name} · {c.area_name}{isAdmin && c.user_name ? ` · ${c.user_name}` : ""}</p>
                <div className="mt-2"><StatusPill status={c.status} /></div>
              </div>
              <span className="tnum font-display text-lg font-semibold">{fmtTemp(c.confirmed_value, c.unit)}</span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Filtros</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Filter label="Local" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v === "__all" ? "" : v, area_id: "", equipment_id: "", shift_id: "" }))} options={opts.locations} testid="f-location" />
            <Filter label="Área" value={filters.area_id} onChange={(v) => set("area_id", v)} options={areas} testid="f-area" />
            <Filter label="Equipo" value={filters.equipment_id} onChange={(v) => set("equipment_id", v)} options={equipment} testid="f-equipment" />
            <Filter label="Turno" value={filters.shift_id} onChange={(v) => set("shift_id", v)} options={shifts} testid="f-shift" />
            {isAdmin && <Filter label="Usuario" value={filters.user_id} onChange={(v) => set("user_id", v)} options={opts.users} testid="f-user" />}
            <div>
              <Label className="text-sm">Resultado</Label>
              <Select value={filters.result || "__all"} onValueChange={(v) => set("result", v)}>
                <SelectTrigger data-testid="f-result" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todos</SelectItem>
                  <SelectItem value="in">Dentro de rango</SelectItem>
                  <SelectItem value="out">Fuera de rango</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Incidencia</Label>
              <Select value={filters.incident || "__all"} onValueChange={(v) => set("incident", v)}>
                <SelectTrigger data-testid="f-incident" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todas</SelectItem>
                  <SelectItem value="open">Incidencias abiertas</SelectItem>
                  <SelectItem value="resolved">Incidencias resueltas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Desde</Label><Input data-testid="f-from" type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="mt-1 h-11" /></div>
              <div><Label className="text-sm">Hasta</Label><Input data-testid="f-to" type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="mt-1 h-11" /></div>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button data-testid="clear-filters-btn" variant="ghost" onClick={() => setFilters(EMPTY)} className="rounded-xl text-slate-500">Limpiar filtros</Button>
            <Button data-testid="apply-filters-btn" onClick={() => setOpen(false)} className="rounded-xl">Ver resultados</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ControlDetail checkId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function Filter({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Select value={value || "__all"} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1 h-11"><SelectValue placeholder="Todos" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all">Todos</SelectItem>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
